import { useState, useRef } from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  EditPolicyFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditPolicyFormData
} from './schema'
import {
  renderTextControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { LiveJsonPreview } from '@/components/rema/shared'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Plus, Trash2 } from 'lucide-react'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

export type EditPolicyComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<EditPolicyFormData>
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof EditPolicyFormData, value: unknown): string | null => {
  const shape = EditPolicyFormSchema.shape[name]
  if (!shape) return null
  const parsed = shape.safeParse(value)
  if (!parsed.success) return parsed.error.issues[0]?.message ?? 'Invalid value'
  return null
}

export function EditPolicyComposer(props: EditPolicyComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  const [allow, setAllow] = useState<string[]>(initialValues?.allow ?? [])
  const [deny, setDeny] = useState<string[]>(initialValues?.deny ?? [])
  const [resourceInstances, setResourceInstances] = useState<string[]>(initialValues?.resourceInstances ?? [])
  const [conditions, setConditions] = useState<Array<{ key: string; value: string }>>(
    Object.entries(initialValues?.conditions ?? {}).map(([key, value]) => ({ key, value: String(value) }))
  )
  const allowRef = useRef(allow)
  allowRef.current = allow
  const denyRef = useRef(deny)
  denyRef.current = deny
  const resourceInstancesRef = useRef(resourceInstances)
  resourceInstancesRef.current = resourceInstances
  const conditionsRef = useRef(conditions)
  conditionsRef.current = conditions

  const { data: rolesData } = useApiQuery('getRoles', {})
  const { data: resourceTypesData } = useApiQuery('getResourceTypes', {})
  const { data: instancesData } = useApiQuery('getResourceInstances', {})

  const roleOptions: Array<{ value: string; label: string }> = (rolesData?.content?.roles ?? []).map((r: any) => ({
    value: r.roleUri ?? `${r.namespace}://role/${r.roleType}/${r.organization}/${r.roleName}`,
    label: r.roleName ?? r.roleUri,
  }))

  const permissionOptions: string[] = Array.from(new Set(
    (resourceTypesData?.content?.resourceTypes ?? []).flatMap((rt: any) => rt.permissions ?? [])
  ))

  const instanceOptions: Array<{ value: string; label: string }> = (instancesData?.content?.resourceInstances ?? []).map((ri: any) => ({
    value: ri.instanceUri ?? ri.resourceInstanceName ?? String(ri.instanceNumber),
    label: ri.resourceInstanceName ?? String(ri.instanceNumber),
  }))

  const upsertPolicy = useApiMutation('upsertPolicy', 'POST', {
    invalidates: ['getPolicies'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to update policy:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: EditPolicyFormSchema,
    defaultValues: {
      policyNumber: initialValues?.policyNumber,
      policyUri: initialValues?.policyUri ?? '',
      version: initialValues?.version ?? '',
      role: initialValues?.role ?? '',
      allow: [],
      deny: [],
      resourceInstances: [],
      conditions: {},
    },
    onSubmit: async ({ value }) => {
      const conditionsObj: Record<string, string> = {}
      for (const { key, value: val } of conditionsRef.current) {
        if (key.trim()) conditionsObj[key.trim()] = val
      }

      const parsed = EditPolicyFormSchema.safeParse({
        policyNumber: initialValues?.policyNumber,
        policyUri: value.policyUri?.trim() ?? '',
        version: value.version?.trim() ?? '',
        role: value.role?.trim() ?? '',
        allow: allowRef.current,
        deny: denyRef.current,
        resourceInstances: resourceInstancesRef.current,
        conditions: conditionsObj,
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      await upsertPolicy.mutateAsync(transformToApiRequest(parsed.data))
    },
  })

  const formValues = form.state.values as any
  const conditionsObj: Record<string, string> = {}
  for (const { key, value } of conditions) {
    if (key.trim()) conditionsObj[key.trim()] = value
  }

  const liveJson = {
    uri: formValues.policyUri ?? '',
    version: formValues.version ?? '',
    role: formValues.role ?? '',
    allow,
    deny,
    resourceInstances,
    conditions: conditionsObj,
  }

  const togglePermission = (perm: string, list: string[], setter: (v: string[]) => void) => {
    setter(list.includes(perm) ? list.filter(p => p !== perm) : [...list, perm])
  }

  const toggleInstance = (uri: string, list: string[], setter: (v: string[]) => void) => {
    setter(list.includes(uri) ? list.filter(i => i !== uri) : [...list, uri])
  }

  const addCondition = () => setConditions(prev => [...prev, { key: '', value: '' }])
  const removeCondition = (idx: number) => setConditions(prev => prev.filter((_, i) => i !== idx))
  const updateCondition = (idx: number, field: 'key' | 'value', val: string) => {
    setConditions(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c))
  }

  const renderFormField = (control: any) => {
    if (control.name === 'role') {
      return (
        <form.AppField
          key="role"
          name="role"
          validators={{ onBlur: ({ value }) => validateField('role', value) || undefined }}
        >
          {(bound) => {
            const { SelectField } = bound
            return (
              <SelectField
                label="Role"
                required
                placeholder="Select a role"
                options={roleOptions}
              />
            )
          }}
        </form.AppField>
      )
    }

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{ onBlur: ({ value }) => validateField(control.name as keyof EditPolicyFormData, value) || undefined }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: ['policyUri', 'version', 'role'].includes(control.name),
            visibility: true,
            disabled: false,
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">{FORM_CONFIG.formDescription}</p>
          </div>
        )}

        {/* Identity fields */}
        <div className="space-y-4">
          {renderFormField({ name: 'policyUri', label: 'Policy URI', placeholder: 'e.g., cnx://policy/user/trilogy-net/read-sensors' })}
          {renderFormField({ name: 'version', label: 'Version', placeholder: 'e.g., 1.0.0' })}
          {renderFormField({ name: 'role', htmlControl: 'select' })}
        </div>

        {/* Allow permissions */}
        {permissionOptions.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Allow Permissions</Label>
            <div className="flex flex-wrap gap-2 rounded-md border p-3">
              {permissionOptions.map((perm) => (
                <div key={perm} className="flex items-center gap-1.5">
                  <Checkbox
                    id={`allow-${perm}`}
                    checked={allow.includes(perm)}
                    onCheckedChange={() => togglePermission(perm, allow, setAllow)}
                  />
                  <label htmlFor={`allow-${perm}`} className="text-xs cursor-pointer">{perm}</label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Deny permissions */}
        {permissionOptions.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Deny Permissions</Label>
            <div className="flex flex-wrap gap-2 rounded-md border p-3">
              {permissionOptions.map((perm) => (
                <div key={perm} className="flex items-center gap-1.5">
                  <Checkbox
                    id={`deny-${perm}`}
                    checked={deny.includes(perm)}
                    onCheckedChange={() => togglePermission(perm, deny, setDeny)}
                  />
                  <label htmlFor={`deny-${perm}`} className="text-xs cursor-pointer">{perm}</label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Resource instances */}
        {instanceOptions.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Resource Instances</Label>
            <div className="flex flex-wrap gap-2 rounded-md border p-3">
              {instanceOptions.map((opt) => (
                <div key={opt.value} className="flex items-center gap-1.5">
                  <Checkbox
                    id={`inst-${opt.value}`}
                    checked={resourceInstances.includes(opt.value)}
                    onCheckedChange={() => toggleInstance(opt.value, resourceInstances, setResourceInstances)}
                  />
                  <label htmlFor={`inst-${opt.value}`} className="text-xs cursor-pointer">{opt.label}</label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conditions key-value editor */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Conditions</Label>
          <div className="space-y-2">
            {conditions.map((cond, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  placeholder="Key"
                  value={cond.key}
                  onChange={(e) => updateCondition(idx, 'key', e.target.value)}
                  className="flex-1"
                />
                <Input
                  placeholder="Value"
                  value={cond.value}
                  onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => removeCondition(idx)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={addCondition}>
              <Plus className="h-3.5 w-3.5" />
              Add Condition
            </Button>
          </div>
        </div>

        <LiveJsonPreview data={liveJson} label="Policy JSON Preview" />

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}
          {renderButtonControl({
            label: upsertPolicy.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? '')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? ''),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}

export { EditPolicyFormSchema, transformToApiRequest, FORM_CONFIG, type EditPolicyFormData } from './schema'
